// src/screens/ReportarScreen.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage, functions } from '../firebaseConfig';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import AIAssistantButton from '../components/Shared/AIAssistantButton';
import { IoArrowBack, IoWarningOutline, IoCloudUploadOutline, IoCameraOutline } from "react-icons/io5";
import './ReportarScreen.css'; // Premium Styles
import moment from 'moment';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';

function ReportarScreen() {
  const [tipo, setTipo] = useState('Mantenimiento');
  const [descripcion, setDescripcion] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState({ originalId: '', motivo: '' });

  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();

  const isSuspended = userData?.suspensionReportes?.activo && 
                      userData.suspensionReportes.hasta && 
                      moment().isBefore(moment(userData.suspensionReportes.hasta.toDate ? userData.suspensionReportes.hasta.toDate() : userData.suspensionReportes.hasta));

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setArchivo(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!descripcion) {
      setMensaje('Error: La descripción no puede estar vacía.');
      return;
    }

    if (!currentUser || !userData) {
      setMensaje('Error: No se pudo identificar al usuario. Intenta iniciar sesión de nuevo.');
      return;
    }

    setEnviando(true);
    setMensaje('Enviando reporte, por favor espera...');
    let archivoURL = '';

    try {
      if (archivo) {
        const storageRef = ref(storage, `reportes/${currentUser.uid}/${Date.now()}_${archivo.name}`);
        const snapshot = await uploadBytes(storageRef, archivo);
        archivoURL = await getDownloadURL(snapshot.ref);
      }

      const reportesCollectionRef = collection(db, 'reportes');
      const newReportRef = await addDoc(reportesCollectionRef, {
        tipo: tipo,
        descripcion: descripcion,
        archivoUrl: archivoURL,
        estatus: 'Pendiente',
        fechaCreacion: Timestamp.now(),
        condominioId: userData.condominioId,
        residenteId: userData.uid || currentUser.uid,
        nombreResidente: userData.nombre,
        unidadResidente: userData.numeroCasa
      });

      setMensaje('Analizando reporte con Asistente de IA...');
      try {
        const analizarReporte = httpsCallable(functions, 'analizarReporteDuplicado');
        const response = await analizarReporte({ reporteId: newReportRef.id });
        const resData = response.data;

        if (resData && resData.duplicado) {
          setDuplicateInfo({
            originalId: resData.originalId,
            motivo: resData.motivo
          });
          setShowDuplicateModal(true);
          setMensaje('');
          setTipo('Mantenimiento');
          setDescripcion('');
          setArchivo(null);
          return;
        }
      } catch (iaErr) {
        console.error("Error al analizar reporte con IA: ", iaErr);
      }

      setMensaje('¡Reporte enviado con éxito!');
      setTipo('Mantenimiento');
      setDescripcion('');
      setArchivo(null);

      // Opcional: Navegar después de unos segundos
      setTimeout(() => {
        navigate('/mis-reportes');
      }, 2000);

    } catch (error) {
      console.error("Error al enviar el reporte: ", error);
      setMensaje(`Error al enviar: ${error.message}`);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/mis-reportes" />

      <main className="ks-main">
        <SafeAreaInsets top={false} bottom={true} />
        
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          <div className="reportar-container-custom">
            {/* Premium Banner (Balanced Size like Lockers/Mi Llave) */}
            <div className="muro-header-stitch">
              <div className="muro-hero-card">
                <button type="button" onClick={() => navigate('/mis-reportes')} className="back-btn-reportar glass" aria-label="Volver">
                  <IoArrowBack size={24} />
                </button>
                <div className="muro-hero-content">
                  <h1>Levantar Reporte</h1>
                  <p>Notifica incidencias o sugerencias</p>
                </div>
                <IoWarningOutline className="muro-hero-icon-decor" />
              </div>
            </div>

            {/* Form Card / Suspension screen */}
            {isSuspended ? (
              <div className="reportar-form-card" style={{ 
                textAlign: 'center', 
                padding: '40px 20px', 
                background: 'rgba(254, 226, 226, 0.15)', 
                backdropFilter: 'blur(16px)', 
                border: '1px solid rgba(239, 68, 68, 0.25)', 
                borderRadius: '24px',
                boxShadow: '0 8px 32px rgba(239, 68, 68, 0.05)'
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  backgroundColor: '#fee2e2', 
                  color: '#dc2626', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 20px auto',
                  boxShadow: '0 4px 10px rgba(220, 38, 38, 0.1)'
                }}>
                  <IoWarningOutline size={32} />
                </div>
                <h2 style={{ fontSize: '1.4rem', color: '#1f2937', fontWeight: 'bold', margin: '0 0 10px 0' }}>
                  Acceso Restringido
                </h2>
                <p style={{ color: '#4b5563', fontSize: '0.95rem', lineHeight: '1.5', margin: '0 0 20px 0', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
                  Tu facultad para levantar reportes (mantenimiento, quejas o sugerencias) ha sido suspendida temporalmente por la administración del condominio.
                </p>
                <div style={{ 
                  background: 'white', 
                  padding: '20px', 
                  borderRadius: '16px', 
                  border: '1px solid #e5e7eb', 
                  textAlign: 'left', 
                  maxWidth: '450px', 
                  margin: '0 auto' 
                }}>
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: '#4b5563', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Motivo de la Suspensión:</strong>
                    <span style={{ color: '#1f2937', fontSize: '0.95rem', display: 'block', marginTop: '2px' }}>{userData.suspensionReportes.motivo}</span>
                  </div>
                  <div>
                    <strong style={{ color: '#4b5563', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fecha de Expiración:</strong>
                    <span style={{ color: '#1f2937', fontSize: '0.95rem', display: 'block', marginTop: '2px' }}>
                      {moment(userData.suspensionReportes.hasta.toDate ? userData.suspensionReportes.hasta.toDate() : userData.suspensionReportes.hasta).format('LLL')} ({moment(userData.suspensionReportes.hasta.toDate ? userData.suspensionReportes.hasta.toDate() : userData.suspensionReportes.hasta).fromNow()})
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="reportar-form-card">
                <form onSubmit={handleSubmit}>

                  <div className="form-group-premium">
                    <label className="form-label-premium">Tipo de Reporte</label>
                    <select
                      value={tipo}
                      onChange={(e) => setTipo(e.target.value)}
                      className="form-select-premium"
                    >
                      <option value="Mantenimiento">Mantenimiento</option>
                      <option value="Incidente">Incidente</option>
                      <option value="Sugerencia">Sugerencia</option>
                    </select>
                  </div>

                  <div className="form-group-premium">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label className="form-label-premium" style={{ margin: 0 }}>Descripción Detallada</label>
                      <AIAssistantButton
                        textoOriginal={descripcion}
                        onSugerenciaAceptada={(nuevoTexto) => setDescripcion(nuevoTexto)}
                        contexto="incidencias"
                      />
                    </div>
                    <textarea
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      placeholder="Describe la situación, ubicación exacta y detalles relevantes..."
                      className="form-textarea-premium"
                      rows={5}
                    />
                  </div>

                  <div className="form-group-premium">
                    <label className="form-label-premium">Evidencia (Opcional)</label>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <div className="file-input-wrapper" style={{ flex: 1, minWidth: '130px' }}>
                        <div className="file-input-button" style={{ padding: '16px 12px', gap: '6px' }}>
                          <IoCloudUploadOutline size={26} />
                          <span style={{ fontSize: '0.8rem' }}>Galería / Archivo</span>
                        </div>
                        <input
                          type="file"
                          onChange={handleFileChange}
                          accept="image/*,.pdf"
                        />
                      </div>
                      <div className="file-input-wrapper" style={{ flex: 1, minWidth: '130px' }}>
                        <div className="file-input-button" style={{ padding: '16px 12px', gap: '6px', background: '#f5f3ff', border: '1px dashed #7B61FF', color: '#7B61FF' }}>
                          <IoCameraOutline size={26} />
                          <span style={{ fontSize: '0.8rem' }}>Tomar Foto</span>
                        </div>
                        <input
                          type="file"
                          onChange={handleFileChange}
                          accept="image/*"
                          capture="environment"
                        />
                      </div>
                    </div>
                    {archivo && (
                      <div style={{ marginTop: '10px', fontSize: '0.82rem', color: '#4b5563', backgroundColor: '#f3f4f6', padding: '8px 12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>📎 Seleccionado:</span> <strong>{archivo.name}</strong>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="form-submit-btn"
                    disabled={enviando}
                  >
                    {enviando ? 'Enviando Reporte...' : 'Enviar Reporte'}
                  </button>

                  {mensaje && (
                    <p className={`form-mensaje ${mensaje.includes('Error') ? 'error' : 'exito'}`}>
                      {mensaje}
                    </p>
                  )}

                </form>
              </div>
            )}
          </div>
        </div>
      </main>

      {showDuplicateModal && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, padding: '20px' }}>
          <div className="modal-content" style={{ backgroundColor: '#ffffff', borderRadius: '24px', padding: '28px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ backgroundColor: '#eff6ff', borderRadius: '50%', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
              <IoWarningOutline size={40} />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0', fontFamily: 'Manrope, sans-serif' }}>Reporte Duplicado Detectado</h3>
            <p style={{ fontSize: '0.92rem', color: '#64748b', lineHeight: '1.5', margin: '0 0 20px 0' }}>
              Nuestro Asistente de IA ha identificado que este problema ya ha sido reportado previamente por otro vecino (coincide con el reporte <strong>#{duplicateInfo.originalId}</strong>).
            </p>
            <div style={{ backgroundColor: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: '16px', padding: '16px', fontSize: '0.88rem', color: '#475569', textAlign: 'left', width: '100%', marginBottom: '24px', lineHeight: '1.4' }}>
              <strong>Explicación de la IA:</strong> {duplicateInfo.motivo}
            </div>
            <button
              onClick={() => {
                setShowDuplicateModal(false);
                navigate('/mis-reportes');
              }}
              style={{ backgroundColor: '#7B61FF', color: '#ffffff', border: 'none', borderRadius: '16px', padding: '14px 24px', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer', width: '100%', transition: 'background-color 0.2s', boxShadow: '0 4px 6px -1px rgba(123, 97, 255, 0.2)' }}
            >
              Entendido, ver mis reportes
            </button>
          </div>
        </div>
      )}

      <KondoBottomNav activeRoute="/mis-reportes" />
    </div>
  );
}

export default ReportarScreen;